import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './UploadImageModal.css';

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.ai', '.psd', '.svg'];
const ACCEPTED_FORMATS    = ['image/jpeg', 'image/png', 'image/svg+xml', 'application/pdf'];
const MAX_SIZE_BYTES       = 50 * 1024 * 1024; // 50 MB

function formatBytes(bytes) {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function isPreviewable(file) {
    if (!file) return false;
    if (typeof file === 'string') return true;
    return ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'].includes(file.type);
}

function UploadImageModal({
    isOpen,
    onClose,
    onConfirm,
    side             = 'front',
    sideLabel        = 'Front',
    imprintLocation  = '',
    allSideLocations = [],
    productConfig    = {},
    existingDesigns  = {},
    rawOriginalURLs  = {},
    savedTransforms  = {},
    editorSettings   = {},
    currentStep      = 0,
}) {
    const navigate     = useNavigate();
    const fileInputRef = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewURL,   setPreviewURL]   = useState(null);
    const [dragOver,     setDragOver]     = useState(false);
    const [error,        setError]        = useState('');

    const handleFile = useCallback((file) => {
        setError('');
        if (!file) return;
        if (file.size > MAX_SIZE_BYTES) {
            setError('File is too large. Maximum allowed size is 50 MB.');
            return;
        }
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!ACCEPTED_FORMATS.includes(file.type) && !ACCEPTED_EXTENSIONS.includes(ext)) {
            setError('Unsupported format. Use JPG, PNG, PDF, AI, PSD, or SVG.');
            return;
        }
        setSelectedFile(file);
        setPreviewURL(isPreviewable(file) ? URL.createObjectURL(file) : null);
    }, []);

    const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
    const handleDrop      = (e) => {
        e.preventDefault(); setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    };

    // stopPropagation prevents this click from bubbling up to the dropzone
    const handleChangeFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        setPreviewURL(null);
        setError('');
    };

    const handleClose = () => {
        setSelectedFile(null); setPreviewURL(null);
        setError(''); setDragOver(false);
        onClose();
    };

    const handleUseFile = () => {
        if (!selectedFile) return;
        onConfirm(imprintLocation, selectedFile);
        handleClose();
    };

    const handleOpenEditor = () => {
        if (!selectedFile) return;
        navigate('/design-editor', {
            state: {
                file: selectedFile,
                previewURL,
                side,
                sideLabel,
                imprintLocation,
                allSideLocations,
                productConfig,
                existingDesigns,
                rawOriginalURLs,
                // Keep fallback chain intact in DesignEditor initializer.
                originalURLs: rawOriginalURLs,
                savedTransforms,
                editorSettings,
                currentStep,
            }
        });
        handleClose();
    };

    const handleOverlayClick = (e) => { if (e.target === e.currentTarget) handleClose(); };

    if (!isOpen) return null;

    return (
        <div className="upload-modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="upload-modal-title">
            <div className="upload-modal">

                <button className="upload-modal-close" onClick={handleClose} aria-label="Close modal">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>

                <div className="upload-modal-header">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 16 12 12 8 16"/>
                        <line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
                    </svg>
                    <div>
                        <h2 className="upload-modal-title" id="upload-modal-title">Upload your Design</h2>
                        <p className="upload-modal-side-label">
                            For: {imprintLocation ? `${sideLabel} — ${imprintLocation}` : sideLabel}
                        </p>
                    </div>
                </div>

                {/* ── Single hidden input, always outside the dropzone ────────────────── */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS.join(',')}
                    onChange={handleInputChange}
                    aria-hidden="true"
                    tabIndex={-1}
                    style={{ display: 'none' }}
                />

                {/* ── Dropzone — onClick only fires when no file is selected ─────────── */}
                <div
                    className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !selectedFile && fileInputRef.current?.click()}
                    role="button"
                    tabIndex={selectedFile ? -1 : 0}
                >
                    {!selectedFile ? (
                        <>
                            <svg className="upload-dropzone-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="12" y1="18" x2="12" y2="12"/>
                                <line x1="9" y1="15" x2="15" y2="15"/>
                            </svg>
                            <p className="upload-dropzone-text">Choose a file or drag and drop it here</p>
                        </>
                    ) : (
                        <div className="upload-preview">
                            {previewURL
                                ? <img src={previewURL} alt="Design preview" className="upload-preview-image" />
                                : (
                                    <div className="upload-preview-icon">
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                    </div>
                                )
                            }
                            <p className="upload-preview-filename">{selectedFile.name}</p>
                            <p className="upload-preview-filesize">{formatBytes(selectedFile.size)}</p>
                            {/* stopPropagation prevents bubbling to dropzone onClick */}
                            <button
                                className="upload-preview-change"
                                onClick={handleChangeFile}
                                type="button"
                            >
                                Change file
                            </button>
                        </div>
                    )}
                </div>

                {error && <p className="upload-error">{error}</p>}

                {!selectedFile && (
                    <button
                        className="upload-browse-btn"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Browse File
                    </button>
                )}

                {selectedFile && (
                    <div className="upload-action-buttons">
                        <button
                            className="upload-use-btn"
                            onClick={handleUseFile}
                            type="button"
                        >
                            ✓ Use This File
                        </button>
                        <button
                            className="upload-confirm-btn"
                            onClick={handleOpenEditor}
                            type="button"
                        >
                            Open in Design Editor →
                        </button>
                    </div>
                )}

                <div className="upload-info-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p>
                        Supported Formats: JPG, PNG, PDF, AI, PSD, SVG — up to 50 MB.<br />
                        To optimize upload speed, save your file as .JPG when possible.<br />
                        For layered files, please flatten all layers prior to submission.
                    </p>
                </div>

            </div>
        </div>
    );
}

export default UploadImageModal;
